/**
 * Color Utility for League Theme Generation
 *
 * This module generates unique, deterministic, muted/pastel colors for each league
 * based on the league name. The same league name will always produce the same color.
 *
 * Usage:
 *   const theme = generateLeagueTheme("Men's 40+");
 *   // Returns: { primary, primaryRgb, light, dark, badge, hue, saturation, lightness }
 */

/**
 * Generate a deterministic hash from a string
 * @param {string} str - The string to hash
 * @returns {number} - A hash value
 */
function hashString(str) {
  let hash = 0;
  if (!str || str.length === 0) return hash;
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash);
}

/**
 * Generate a muted, pastel color based on a league name
 * @param {string} leagueName - The name of the league
 * @returns {object} - An object containing various color formats
 */
export function generateLeagueTheme(leagueName) {
  if (!leagueName) {
    // Default theme if no league name
    return {
      primary: 'hsl(145, 63%, 42%)', // Default green
      primaryRgb: '46, 204, 113',
      light: 'hsl(145, 63%, 95%)',
      dark: 'hsl(145, 63%, 35%)',
      badge: 'linear-gradient(135deg, hsl(145, 63%, 42%) 0%, hsl(145, 63%, 35%) 100%)',
    };
  }

  const hash = hashString(leagueName);
  
  // Generate hue from hash (0-360)
  const hue = hash % 360;
  
  // Use moderate saturation for muted colors (35-50%)
  const saturation = 35 + (hash % 16);
  
  // Use high lightness for pastel colors (55-70%)
  const lightness = 55 + (hash % 16);
  
  // Create the primary color
  const primary = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  
  // Create variations
  const light = `hsl(${hue}, ${saturation}%, 95%)`; // Very light for backgrounds
  const dark = `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`; // Darker for borders/emphasis
  const badge = `linear-gradient(135deg, hsl(${hue}, ${saturation}%, ${lightness}%) 0%, hsl(${hue}, ${saturation}%, ${lightness - 10}%) 100%)`;
  
  // Convert primary to RGB for use in rgba()
  const primaryRgb = hslToRgb(hue, saturation, lightness);
  
  return {
    primary,
    primaryRgb,
    light,
    dark,
    badge,
    hue,
    saturation,
    lightness,
  };
}

/**
 * Convert HSL to RGB
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - RGB values as "r, g, b"
 */
function hslToRgb(h, s, l) {
  s /= 100;
  l /= 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  return `${r}, ${g}, ${b}`;
}

